import {
  GetOrderQuery,
  type IGetOrderQueryResponse,
} from '@fhs-legacy/frontend/src/remote/queries/order';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import { Network } from '@fhs-legacy/frontend/src/utils/network/status';
import { IServerOrder } from '@rbi-ctg/menu';

import { ORDER_SUBMITTED_STATUSES } from '../order-status';

export const RECOVERY_WINDOW_MS = 90000;
const RETRY_DELAY_MS = 3000;
const QUERY_TIMEOUT_MS = 5000;

type SubmittedOrderLookup =
  | { status: 'submitted'; order: IServerOrder }
  | { status: 'notSubmitted' };

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function readCachedOrder(rbiOrderId: string): IServerOrder | null {
  try {
    const cached = legacyClient.readQuery<IGetOrderQueryResponse>({
      query: GetOrderQuery,
      variables: { rbiOrderId },
    });
    return cached?.order ?? null;
  } catch {
    return null;
  }
}

function asSubmitted(order: IServerOrder | null | undefined): SubmittedOrderLookup | null {
  if (order?.status && ORDER_SUBMITTED_STATUSES.has(order.status)) {
    return { status: 'submitted', order };
  }
  return null;
}

async function queryOrderStatusFromNetwork(rbiOrderId: string, timeoutMs: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Order status lookup timed out')), timeoutMs);
  });

  try {
    return await Promise.race([
      legacyClient.query<IGetOrderQueryResponse>({
        query: GetOrderQuery,
        variables: { rbiOrderId },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      }),
      timeoutPromise,
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export async function recoverSubmittedOrder(rbiOrderId: string): Promise<SubmittedOrderLookup> {
  let preCommitSince: number | null = null;

  const cachedSubmitted = asSubmitted(readCachedOrder(rbiOrderId));
  if (cachedSubmitted) {
    return cachedSubmitted;
  }

  while (true) {
    const networkStatus = await Network.getStatus();
    if (!networkStatus.connected) {
      preCommitSince = null;
      const cached = asSubmitted(readCachedOrder(rbiOrderId));
      if (cached) {
        return cached;
      }
      await delay(RETRY_DELAY_MS);
      continue;
    }

    try {
      const response = await queryOrderStatusFromNetwork(rbiOrderId, QUERY_TIMEOUT_MS);
      const order = response.data?.order;

      if (order?.status && ORDER_SUBMITTED_STATUSES.has(order.status)) {
        return { status: 'submitted', order };
      }

      if (order?.status) {
        if (preCommitSince === null) {
          preCommitSince = Date.now();
        }
        if (Date.now() - preCommitSince >= RECOVERY_WINDOW_MS) {
          return { status: 'notSubmitted' };
        }
      } else {
        preCommitSince = null;
      }
    } catch (error) {
      logger.warn({
        message: '[RecoverSubmittedOrder] Order status lookup failed, still confirming',
        rbiOrderId,
        error: error instanceof Error ? error.message : String(error),
      });
      preCommitSince = null;
      const cached = asSubmitted(readCachedOrder(rbiOrderId));
      if (cached) {
        return cached;
      }
    }

    await delay(RETRY_DELAY_MS);
  }
}

export async function lookupSubmittedOrderOnce(
  rbiOrderId: string
): Promise<SubmittedOrderLookup | { status: 'unavailable' }> {
  const cachedSubmitted = asSubmitted(readCachedOrder(rbiOrderId));
  if (cachedSubmitted) {
    return cachedSubmitted;
  }

  try {
    const networkStatus = await Network.getStatus();
    if (!networkStatus.connected) {
      return { status: 'unavailable' };
    }

    const response = await queryOrderStatusFromNetwork(rbiOrderId, QUERY_TIMEOUT_MS);
    const order = response.data?.order;
    if (order?.status && ORDER_SUBMITTED_STATUSES.has(order.status)) {
      return { status: 'submitted', order };
    }
    if (order?.status) {
      return { status: 'notSubmitted' };
    }
    return { status: 'unavailable' };
  } catch (error) {
    logger.warn({
      message: '[RecoverSubmittedOrder] One-shot order status lookup failed',
      rbiOrderId,
      error: error instanceof Error ? error.message : String(error),
    });
    const cached = asSubmitted(readCachedOrder(rbiOrderId));
    if (cached) {
      return cached;
    }
    return { status: 'unavailable' };
  }
}
